import {
  Course,
  Status,
  Milestone,
  Lesson,
  DevExperienceEnum,
  BuildTypeEnum,
  ModelingExperienceEnum,
  SurveyAnswer,
} from '@modules/learning/types/learning';
import {
  SurveyAnswersApi,
  SurveyAnswerModel,
  SurveyAnswersCreateOrUpdateSurveyAnswersRequest,
  LessonProgressApi,
  LessonProgressUpdateLessonProgressRequest,
  LessonProgressUpdateModel,
  CoursesApi,
} from '@rbx/clients/docSiteFeedsApi';
import {
  getLocalSurveyAnswers,
  setLocalSurveyAnswers,
  clearLocalSurveyAnswers,
  getLocalCourses,
  setLocalCourse,
  getLocalLearningProgress,
  updateLocalLessonProgress,
  setLocalLearningProgress,
} from '@modules/learning/utils/localStorageSync';
import { getCourseByCourseId, CourseId } from '@modules/learning/utils/courseUtils';
import { getBEDEV2ServiceBasePath } from '@modules/utilities/constants/baseUrl';
import { Configuration } from '@rbx/clients';

const apiToInternalSurveyAnswer = (apiAnswer: SurveyAnswerModel): SurveyAnswer => {
  const buildTypeMap: Record<string, BuildTypeEnum> = {
    Invalid: BuildTypeEnum.BUILD_TYPE_ENUM_INVALID,
    Experience: BuildTypeEnum.BUILD_TYPE_ENUM_EXPERIENCE,
    Modeling: BuildTypeEnum.BUILD_TYPE_ENUM_MODELING,
    General: BuildTypeEnum.BUILD_TYPE_ENUM_GENERAL,
  };

  const devExperienceMap: Record<string, DevExperienceEnum> = {
    Invalid: DevExperienceEnum.DEV_EXPERIENCE_ENUM_INVALID,
    Beginner: DevExperienceEnum.DEV_EXPERIENCE_ENUM_BEGINNER,
    Intermediate: DevExperienceEnum.DEV_EXPERIENCE_ENUM_INTERMEDIATE,
    Advanced: DevExperienceEnum.DEV_EXPERIENCE_ENUM_ADVANCED,
  };

  const modelingExperienceMap: Record<string, ModelingExperienceEnum> = {
    Invalid: ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_INVALID,
    Beginner: ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_BEGINNER,
    Intermediate: ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_INTERMEDIATE,
    Advanced: ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_ADVANCED,
  };

  const surveyData = apiAnswer.surveyAnswers || {};

  return {
    id: apiAnswer.id,
    buildType:
      buildTypeMap[(surveyData.buildType as string) || ''] ?? BuildTypeEnum.BUILD_TYPE_ENUM_INVALID,
    devExperience:
      devExperienceMap[(surveyData.devExperience as string) || ''] ??
      DevExperienceEnum.DEV_EXPERIENCE_ENUM_INVALID,
    modelingExperience:
      modelingExperienceMap[(surveyData.modelingExperience as string) || ''] ??
      ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_INVALID,
  };
};

const internalToApiSurveyAnswer = (internalAnswer: SurveyAnswer): SurveyAnswerModel => {
  const buildTypeReverseMap: Record<BuildTypeEnum, string> = {
    [BuildTypeEnum.BUILD_TYPE_ENUM_INVALID]: 'Invalid',
    [BuildTypeEnum.BUILD_TYPE_ENUM_EXPERIENCE]: 'Experience',
    [BuildTypeEnum.BUILD_TYPE_ENUM_MODELING]: 'Modeling',
    [BuildTypeEnum.BUILD_TYPE_ENUM_GENERAL]: 'General',
  };

  const devExperienceReverseMap: Record<DevExperienceEnum, string> = {
    [DevExperienceEnum.DEV_EXPERIENCE_ENUM_INVALID]: 'Invalid',
    [DevExperienceEnum.DEV_EXPERIENCE_ENUM_BEGINNER]: 'Beginner',
    [DevExperienceEnum.DEV_EXPERIENCE_ENUM_INTERMEDIATE]: 'Intermediate',
    [DevExperienceEnum.DEV_EXPERIENCE_ENUM_ADVANCED]: 'Advanced',
  };

  const modelingExperienceReverseMap: Record<ModelingExperienceEnum, string> = {
    [ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_INVALID]: 'Invalid',
    [ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_BEGINNER]: 'Beginner',
    [ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_INTERMEDIATE]: 'Intermediate',
    [ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_ADVANCED]: 'Advanced',
  };

  const surveyAnswers: Record<string, string> = {
    buildType: buildTypeReverseMap[internalAnswer.buildType],
  };

  if (
    internalAnswer.buildType === BuildTypeEnum.BUILD_TYPE_ENUM_EXPERIENCE ||
    internalAnswer.buildType === BuildTypeEnum.BUILD_TYPE_ENUM_GENERAL
  ) {
    if (internalAnswer.devExperience !== DevExperienceEnum.DEV_EXPERIENCE_ENUM_INVALID) {
      surveyAnswers.devExperience = devExperienceReverseMap[internalAnswer.devExperience];
    }
  }

  if (internalAnswer.buildType === BuildTypeEnum.BUILD_TYPE_ENUM_MODELING) {
    if (
      internalAnswer.modelingExperience !== ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_INVALID
    ) {
      surveyAnswers.modelingExperience =
        modelingExperienceReverseMap[internalAnswer.modelingExperience];
    }
  }

  return {
    ...(internalAnswer.id !== undefined ? { id: internalAnswer.id } : {}),
    surveyAnswers,
  };
};

const apiStringToStatus = (apiStatus: string): Status => {
  const statusMap: Record<string, Status> = {
    Invalid: Status.STATUS_ENUM_INVALID,
    NotStarted: Status.STATUS_ENUM_NOT_STARTED,
    InProgress: Status.STATUS_ENUM_IN_PROGRESS,
    Completed: Status.STATUS_ENUM_COMPLETED,
    Recommended: Status.STATUS_ENUM_RECOMMENDED,
  };
  return statusMap[apiStatus] ?? Status.STATUS_ENUM_INVALID;
};

const statusToApiString = (status: Status): string => {
  const statusMap: Record<Status, string> = {
    [Status.STATUS_ENUM_INVALID]: 'Invalid',
    [Status.STATUS_ENUM_NOT_STARTED]: 'NotStarted',
    [Status.STATUS_ENUM_IN_PROGRESS]: 'InProgress',
    [Status.STATUS_ENUM_COMPLETED]: 'Completed',
    [Status.STATUS_ENUM_RECOMMENDED]: 'Recommended',
  };
  return statusMap[status] ?? 'Invalid';
};

const internalToApiLessonProgress = (
  lessonPath: string,
  status: Status,
): LessonProgressUpdateModel => ({
  lessonUrl: lessonPath,
  status: statusToApiString(status),
});

export class LearningClient {
  private basePath: string;

  private config: RequestInit;

  private surveyAnswersApi: SurveyAnswersApi;

  private lessonProgressApi: LessonProgressApi;

  private coursesApi: CoursesApi;

  constructor() {
    this.basePath = getBEDEV2ServiceBasePath('doc-site');
    this.config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const apiConfig = new Configuration({
      basePath: this.basePath,
      credentials: 'include',
    });

    this.surveyAnswersApi = new SurveyAnswersApi(apiConfig);
    this.lessonProgressApi = new LessonProgressApi(apiConfig);
    this.coursesApi = new CoursesApi(apiConfig);
  }

  async getSurveyAnswers(): Promise<SurveyAnswer | null> {
    try {
      const response = await this.surveyAnswersApi.surveyAnswersGetSurveyAnswers();

      if (response.answer) {
        const internalAnswer = apiToInternalSurveyAnswer(response.answer);
        setLocalSurveyAnswers(internalAnswer);
        return internalAnswer;
      }

      return getLocalSurveyAnswers();
    } catch (error) {
      console.error('[LearningClient] Error fetching survey answers:', error);
      return getLocalSurveyAnswers();
    }
  }

  async saveSurveyAnswers(answers: SurveyAnswer): Promise<void> {
    setLocalSurveyAnswers(answers);

    try {
      const apiAnswer = internalToApiSurveyAnswer(answers);
      const request: SurveyAnswersCreateOrUpdateSurveyAnswersRequest = {
        answer: apiAnswer,
      };

      await this.surveyAnswersApi.surveyAnswersCreateOrUpdateSurveyAnswers({
        surveyAnswersCreateOrUpdateSurveyAnswersRequest: request,
      });
    } catch (error) {
      console.error('[LearningClient] Error saving survey answers:', error);
    }
  }

  async getCourseIds(filterIds?: CourseId[]): Promise<CourseId[]> {
    try {
      const response = await this.coursesApi.coursesListCourses();

      if (response.paths && response.paths.length > 0) {
        const allCourseIds = response.paths as CourseId[];
        if (filterIds && filterIds.length > 0) {
          return allCourseIds.filter((courseId) => filterIds.includes(courseId));
        }

        return allCourseIds;
      }

      return [];
    } catch (error) {
      console.error('[LearningClient] Error fetching course IDs from API:', error);
      return [];
    }
  }

  async clearSurveyAnswers(): Promise<void> {
    clearLocalSurveyAnswers();
    try {
      await this.surveyAnswersApi.surveyAnswersDeleteSurveyAnswers();
    } catch (error) {
      console.warn(
        'Failed to clear survey answers from server, but cleared from localStorage:',
        error,
      );
    }
  }

  async getCourses(filterIds?: CourseId[]): Promise<Course[]> {
    try {
      const courseIds = await this.getCourseIds(filterIds);

      if (courseIds.length === 0) {
        console.warn('[LearningClient] No course IDs found, returning empty array');
        return [];
      }

      const progress = getLocalLearningProgress();

      const courses = courseIds
        .map((courseId) => {
          const template = getCourseByCourseId(courseId);
          if (!template) return null;

          return {
            courseId,
            isCompleted: Status.STATUS_ENUM_NOT_STARTED,
            milestones: template.milestones.map((milestone: Milestone) => ({
              ...milestone,
              lessons: milestone.lessons.map((lesson: Lesson) => ({
                ...lesson,
                isCompleted: progress?.[lesson.path] ?? Status.STATUS_ENUM_NOT_STARTED,
              })),
            })),
          } as Course;
        })
        .filter((course): course is Course => course !== null);

      if (courses.length > 0) {
        setLocalCourse(courses[0]);
      }

      return courses;
    } catch (error) {
      console.error('[LearningClient] Error getting courses:', error);
      const localPaths = getLocalCourses();
      return localPaths ?? [];
    }
  }

  async getLessonProgress(): Promise<Record<string, Status>> {
    try {
      const response = await this.lessonProgressApi.lessonProgressGetLessonProgress();

      if (response.lessonProgress) {
        const internalProgress: Record<string, Status> = {};
        Object.entries(response.lessonProgress).forEach(([lessonPath, apiStatus]) => {
          internalProgress[lessonPath] = apiStringToStatus(apiStatus);
        });

        setLocalLearningProgress(internalProgress);
        return internalProgress;
      }

      console.warn('[LearningClient] No lesson progress found, returning local progress');
      const localProgress = getLocalLearningProgress();
      return localProgress;
    } catch (error) {
      console.error('[LearningClient] Error fetching lesson progress from server:', error);
      return getLocalLearningProgress();
    }
  }

  async updateLessonProgress(lessonPath: string, status: Status): Promise<void> {
    try {
      const updateModel = internalToApiLessonProgress(lessonPath, status);
      const request: LessonProgressUpdateLessonProgressRequest = {
        updates: [updateModel],
      };

      await this.lessonProgressApi.lessonProgressUpdateLessonProgress({
        lessonProgressUpdateLessonProgressRequest: request,
      });

      updateLocalLessonProgress(lessonPath, status);
      window.dispatchEvent(new CustomEvent('learningProgressUpdate'));
    } catch (error) {
      console.error('[LearningClient] Error updating lesson progress on server:', error);
      throw error;
    }
  }
}

const learningClient = new LearningClient();
export default learningClient;
